import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "react-alert";
import "./ManageOrg.scss";
import { useReducer } from "react";
import Input from "../Input";
import Dropdown from "../Dropdown";
import HappyHourSetupModal from "./HappyHourSetupModal";
import ModalComponent from "../modal/Modal";
import { initialOrgConfigState, orgConfigReducer } from "./ManageOrgReducer";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { ipv4Validation, portValidation } from "../../helper/regexValidations";
import { uploadMedia } from "../../utility/uploadMedia";
import deleteImage from "../../utility/deleteImage";

const MAX_IDLE_IMAGE_SIZE_MB = 1;
const MAX_IDLE_IMAGE_SIZE_O = 1024 * 1024 * MAX_IDLE_IMAGE_SIZE_MB;

function ManageOrg({ selectedOrg }) {
  const { t } = useTranslation("manage");

  const [isHappyHourModalOpen, setIsHappyHourModalOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const fileInputRef = useRef(null);

  const [orgConfig, dispatch] = useReducer(orgConfigReducer, initialOrgConfigState);

  useEffect(() => {
    if (selectedOrg) {
      dispatch({
        type: "SET_CONFIG",
        payload: { ...selectedOrg },
      });
    }
  }, [selectedOrg]);

  const {
    fastpassActivated,
    isSlateActive,
    kioskActivated,
    neptingCode,
    neptingId,
    openingHours,
    printerIp,
    printerPort,
    isUsbPrinter,
    printingActivated,
    showVivaReceipt,
    skipExchangeActivated,
    vivaMerchantId = null,
    idleImageUrl,
    pinCode,
  } = orgConfig;

  const paymentProvider = () => {
    if (orgConfig.vivawalletActivated) return "viva";
    if (orgConfig.adyenActivated) return "adyen";
    if (orgConfig.neptingActivated) return "nepting";
    else return "sumup";
  };

  const printerProvider = () => {
    if (orgConfig.printerIp !== null && orgConfig.printerPort !== null) return "printer_remote";
    if (orgConfig.isUsbPrinter) return "printer_usb";
    else return "printer_none";
  };

  const onPrinterTypeChange = (printer) => {
    if (printer.value === printerProvider()) return;

    const payload = {
      isUsbPrinter: printer.value === "printer_usb",
      printerIp: printer.value === "printer_remote" ? "" : null,
      printerPort: printer.value === "printer_remote" ? "" : null,
    };

    dispatch({ type: "UPDATE_PRINTER_CONFIG", payload });
  };

  const printerTypeOptions = [
    { value: "printer_usb", label: t("printer_usb") },
    { value: "printer_remote", label: t("printer_remote") },
    { value: "printer_none", label: t("printer_none") },
  ];

  const paymentTypeOptions = [
    { value: "sumup", label: t("sumup") },
    { value: "viva", label: t("viva_com") },
    { value: "adyen", label: t("adyen") },
    { value: "nepting", label: t("nepting") },
  ];

  const selectedPaymentType = paymentTypeOptions.find((opt) => opt.value === paymentProvider());
  const selectedPrinterType = printerTypeOptions.find((opt) => opt.value === printerProvider());

  const updateOrgData = async (data) => {
    const orgDocRef = doc(db, "Organizations", selectedOrg.id);
    await updateDoc(orgDocRef, data);
  };

  const alert = useAlert();
  const showLoadingAlert = () => alert.show(t("loading"), { type: "info" });

  const updateSlateHandler = async (active) => {
    showLoadingAlert();
    try {
      await updateOrgData({
        slate: active,
      });
      dispatch({ type: "UPDATE_SLATE", payload: active });
      alert.show(t("slate_updated"), {
        type: "success",
      });
    } catch (err) {
      alert.show(t("slate_not_updated"), {
        type: "error",
      });
      console.error("Error updating document: ", err);
    }
  };

  const updateVivaHandler = async (active) => {
    showLoadingAlert();
    try {
      await updateOrgData({
        vivawalletActivated: active,
        neptingActivated: false,
        adyenActivated: false,
      });
      dispatch({ type: "UPDATE_VIVA_ACTIVATED", payload: active });
      alert.show(t("payment_provider_updated"), {
        type: "success",
      });
    } catch (err) {
      alert.show(t("payment_provider_not_updated"), {
        type: "error",
      });
      console.error("Error updating document: ", err);
    }
  };

  const updateAdyenHandler = async (active) => {
    showLoadingAlert();
    try {
      await updateOrgData({
        adyenActivated: active,
        neptingActivated: false,
        vivawalletActivated: false,
      });
      dispatch({ type: "UPDATE_ADYEN_ACTIVATED", payload: active });
      alert.show(t("payment_provider_updated"), {
        type: "success",
      });
    } catch (err) {
      alert.show(t("payment_provider_not_updated"), {
        type: "error",
      });
      console.error("Error updating document: ", err);
    }
  };

  const updateNeptingHandler = async (active) => {
    showLoadingAlert();
    try {
      await updateOrgData({
        neptingActivated: active,
        adyenActivated: false,
        vivawalletActivated: false,
      });
      dispatch({ type: "UPDATE_NEPTING_ACTIVATED", payload: active });
      alert.show(t("payment_provider_updated"), {
        type: "success",
      });
    } catch (err) {
      alert.show(t("payment_provider_not_updated"), {
        type: "error",
      });
      console.error("Error updating document: ", err);
    }
  };

  const updateSumupHandler = async () => {
    showLoadingAlert();
    try {
      await updateOrgData({
        neptingActivated: false,
        adyenActivated: false,
        vivawalletActivated: false,
      });
      dispatch({ type: "UPDATE_SUMUP_ACTIVATED" });
      alert.show(t("payment_provider_updated"), { type: "success" });
    } catch (err) {
      alert.show(t("payment_provider_not_updated"), { type: "error" });
      console.error("Error updating document: ", err);
    }
  };

  const updateFastpassHandler = async (active) => {
    showLoadingAlert();
    try {
      await updateOrgData({
        fastpassActivated: active,
      });
      dispatch({ type: "UPDATE_FASTPASS_ACTIVATED", payload: active });
      alert.show(t("fastpass_updated"), {
        type: "success",
      });
    } catch (err) {
      alert.show(t("fastpass_not_updated"), {
        type: "error",
      });
      console.error("Error updating document: ", err);
    }
  };

  const updatePrintingHandler = async (active) => {
    showLoadingAlert();
    try {
      await updateOrgData({
        printingActivated: active,
      });
      dispatch({ type: "UPDATE_PRINTING_ACTIVATED", payload: active });
      alert.show(t("printing_updated"), {
        type: "success",
      });
    } catch (err) {
      alert.show(t("printing_not_updated"), {
        type: "error",
      });
      console.error("Error updating document: ", err);
    }
  };

  const updateSkipExchangeHandler = async (active) => {
    showLoadingAlert();
    try {
      await updateOrgData({
        skipExchangeActivated: active,
      });
      dispatch({ type: "UPDATE_SKIP_EXCHANGE_ACTIVATED", payload: active });
      alert.show(t("skip_exchange_updated"), {
        type: "success",
      });
    } catch (err) {
      alert.show(t("skip_exchange_not_updated"), {
        type: "error",
      });
      console.error("Error updating document: ", err);
    }
  };

  const updatePrinterTypeHandler = async () => {
    showLoadingAlert();
    if (
      printerProvider() === "printer_remote" &&
      (!ipv4Validation(orgConfig.printerIp) || !portValidation(orgConfig.printerPort))
    ) {
      alert.show(t("invalid_ip_or_port"), { type: "error" });
      return;
    }

    try {
      await updateOrgData({
        printerIp,
        printerPort,
        isUsbPrinter,
      });
      alert.show(t("printer_type_updated"), {
        type: "success",
      });
    } catch (err) {
      alert.show(t("printing_not_updated"), {
        type: "error",
      });
      console.error("Error updating document: ", err);
    }
  };

  const updateNeptingIdHandler = async () => {
    showLoadingAlert();
    try {
      await updateOrgData({
        neptingId: neptingId,
        neptingCode: neptingCode,
      });
      alert.show(t("neptingId_updated"), {
        type: "success",
      });
    } catch (err) {
      alert.show(t("payment_provider_not_updated"), {
        type: "error",
      });
      console.error("Error updating document: ", err);
    }
  };

  const updateVivaIdHandler = async () => {
    showLoadingAlert();
    try {
      await updateOrgData({
        vivaMerchantId: vivaMerchantId,
      });
      alert.show(t("vivaId_updated"), {
        type: "success",
      });
    } catch (err) {
      alert.show(t("payment_provider_not_updated"), {
        type: "error",
      });
      console.error("Error updating document: ", err);
    }
  };

  const updateOpeningHoursHandler = async () => {
    showLoadingAlert();
    try {
      await updateOrgData({
        openingHours: { start: openingHours.start, end: openingHours.end },
      });
      alert.show(t("opening_hours_updated"), {
        type: "success",
      });
    } catch (err) {
      alert.show(t("opening_hours_not_updated"), {
        type: "error",
      });
      console.error("Error updating document: ", err);
    }
  };

  const resetOpeningHoursHandler = async () => {
    showLoadingAlert();
    try {
      await updateOrgData({
        openingHours: null,
      });
      dispatch({ type: "RESET_OPENINGHOURS" });
      alert.show(t("opening_hours_reset"), {
        type: "success",
      });
    } catch (err) {
      alert.show(t("opening_hours_not_reset"), {
        type: "error",
      });
      console.error("Error resetting document: ", err);
    }
  };

  const updateKioskHandler = async (active) => {
    showLoadingAlert();
    try {
      await updateOrgData({
        kioskActivated: active,
      });
      dispatch({
        type: "UPDATE_KIOSK_ACTIVATED",
        payload: active,
      });
      alert.show(t("kiosk_updated"), {
        type: "success",
      });
    } catch (err) {
      alert.show(t("kiosk_not_updated"), {
        type: "error",
      });
      console.error("Error updating document: ", err);
    }
  };

  const updatePinCodeHandler = async () => {
    showLoadingAlert();
    let pinFormatted = typeof pinCode === "string" ? pinCode.trim() : null;

    if (pinFormatted !== "") {
      if (!pinFormatted) {
        alert.show(t("pin_code_required_to_confirm"), { type: "error" });
        return;
      }

      if (isNaN(Number(pinFormatted))) {
        alert.show(t("pin_code_digits_only"), { type: "error" });
        return;
      }

      if (pinFormatted.length < 4 || pinFormatted.length > 8) {
        alert.show(t("pin_code_length_between_4_8"), { type: "error" });
        return;
      }
    } else {
      pinFormatted = null;
    }

    try {
      await updateOrgData({ pinCode: pinFormatted });
      dispatch({ type: "UPDATE_PIN_CODE", payload: pinFormatted });
      alert.show(t("pin_code_updated"), { type: "success" });
    } catch (err) {
      alert.show(t("pin_code_not_updated"), { type: "error" });
      console.error("Error updating document: ", err);
    }
  };

  const updateShowVivaReceiptHandler = async (active) => {
    showLoadingAlert();
    try {
      await updateOrgData({
        showVivaReceipt: active,
      });
      dispatch({
        type: "UPDATE_SHOW_VIVA_RECEIPT",
        payload: active,
      });
      alert.show(t("show_receipt_updated"), {
        type: "success",
      });
    } catch (err) {
      alert.show(t("show_receipt_not_updated"), {
        type: "error",
      });
      console.error("Error updating document: ", err);
    }
  };

  const onPaymentProviderChange = (option) => {
    if (option.value === paymentProvider()) {
      return;
    }

    switch (option.value) {
      case "sumup":
        updateSumupHandler();
        break;
      case "viva":
        updateVivaHandler(true);
        break;
      case "adyen":
        updateAdyenHandler(true);
        break;
      case "nepting":
        updateNeptingHandler(true);
        break;
      default:
        return;
    }
  };

  const updateIdleImageHandler = async (url = null) => {
    try {
      await updateOrgData({ idleImageUrl: url });
      dispatch({ type: "UPDATE_IDLE_IMAGE", payload: url });
      alert.show(t("idle_screen_image_update_success"), { type: "success" });
    } catch (err) {
      alert.show(t("idle_screen_image_update_error"), { type: "error" });
      console.error("Error updating document: ", err);
    }
  };

  const handleImageSelect = async (event) => {
    showLoadingAlert();
    const file = event.target.files?.[0];

    if (!file || !file.type.startsWith("image/")) {
      alert.show(t("idle_screen_image_select"), { type: "error" });
      return;
    }

    if (file.size > MAX_IDLE_IMAGE_SIZE_O) {
      alert.show(t("idle_screen_image_size_limit", { maxSize: MAX_IDLE_IMAGE_SIZE_MB }), {
        type: "error",
      });
      return;
    }

    try {
      const path = await uploadMedia(
        URL.createObjectURL(file),
        `idleImage/${selectedOrg.id}`,
        false,
      );
      await updateIdleImageHandler(path);
    } catch (error) {
      console.error("Error updating document: ", error);
      alert.show(t("idle_screen_image_upload_error"), { type: "error" });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleImageDeletion = async () => {
    showLoadingAlert();
    if (!idleImageUrl) {
      alert.show(t("idle_screen_image_delete_none"), { type: "error" });
      return;
    }

    try {
      await deleteImage(`idleImage/${selectedOrg.id}`);
      await updateIdleImageHandler(null);
    } catch (error) {
      console.error("Error updating document: ", error);
      alert.show(t("idle_screen_image_delete_error"), { type: "error" });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="manage-org">
      <ModalComponent
        isOpen={imageModalOpen}
        title={t("idle_screen_image_logo")}
        actionsComponent={
          <img
            src={idleImageUrl}
            style={{
              maxHeight: "500px",
              maxWidth: "100%",
              objectFit: "contain",
            }}
          />
        }
        onClose={() => setImageModalOpen(false)}
        shouldNotCloseOnOverlayClick
      />

      <h4>{t("configuration")}</h4>
      <div className="item">
        <form onSubmit={(e) => e.preventDefault()}>
          <h5 className="subtitle">{t("opening_hours")}</h5>
          <div className="text">{t("opening_hours_description")}</div>
          <div className="mb-20">
            <Input
              label={t("start_hour")}
              id="openingStartHour"
              type="time"
              value={openingHours.start}
              onChange={(e) =>
                dispatch({
                  type: "UPDATE_OPENINGHOURS",
                  payload: {
                    end: openingHours.end,
                    start: e.target.value,
                  },
                })
              }
            />
          </div>
          <div className="mb-20">
            <Input
              label={t("end_hour")}
              id="openingEndHour"
              type="time"
              value={openingHours.end}
              onChange={(e) =>
                dispatch({
                  type: "UPDATE_OPENINGHOURS",
                  payload: {
                    end: e.target.value,
                    start: openingHours.start,
                  },
                })
              }
            />
          </div>
          <button onClick={updateOpeningHoursHandler} className="themeBtn m-0 w-100 pos-submit-btn">
            {t("update")}
          </button>
          <button
            type="button"
            onClick={resetOpeningHoursHandler}
            className="themeBtn btn-outline-danger m-0 w-100 pos-submit-btn mt-2"
          >
            {t("reset")}
          </button>
        </form>
      </div>
      <div className="item">
        <form onSubmit={(e) => e.preventDefault()}>
          <h5 className="subtitle">{t("happy_hour")}</h5>
          <div className="text">{t("happy_hour_description")}</div>
          <button
            onClick={() => setIsHappyHourModalOpen(true)}
            className="themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn"
          >
            {t("setup")}
          </button>
        </form>
      </div>

      <div>
        <div className="item">
          <h5 className="subtitle">{t("activate_debt_slate")}</h5>
          <div className="text">{t("allow_debt_slate")}</div>
          <div className="mb-28 gap-12px d-flex filter-buttons">
            <button
              type="button"
              className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${!isSlateActive ? "active" : ""}`}
              onClick={() => {
                updateSlateHandler(false);
              }}
            >
              {t("disabled")}
            </button>
            <button
              type="button"
              className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${isSlateActive ? "active" : ""}`}
              onClick={() => {
                updateSlateHandler(true);
              }}
            >
              {t("enabled")}
            </button>
          </div>
        </div>
      </div>
      <div>
        <div className="item">
          <h5 className="subtitle">{t("payment_provider")}</h5>
          <div className="text">{t("choose_card_payment_provider")}</div>
          <Dropdown
            onChange={onPaymentProviderChange}
            value={selectedPaymentType}
            options={paymentTypeOptions}
          />
        </div>

        <div className="item">
          <h5 className="subtitle">{t("activate_fastpass")}</h5>
          <div className="text">{t("allow_clients_order_via_phone")}</div>
          <div className="mb-28 gap-12px d-flex filter-buttons">
            <button
              type="button"
              className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${!fastpassActivated ? "active" : ""}`}
              onClick={() => {
                updateFastpassHandler(false);
              }}
            >
              {t("disabled")}
            </button>
            <button
              type="button"
              className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${fastpassActivated ? "active" : ""}`}
              onClick={() => {
                updateFastpassHandler(true);
              }}
            >
              {t("enabled")}
            </button>
          </div>
        </div>
        <div className="item">
          <h5 className="subtitle">{t("activate_receipt_printing")}</h5>
          <div className="text">{t("printing_only_compatible")}</div>
          <div className="mb-28 gap-12px d-flex filter-buttons">
            <button
              type="button"
              className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${!printingActivated ? "active" : ""}`}
              onClick={() => {
                updatePrintingHandler(false);
              }}
            >
              {t("disabled")}
            </button>
            <button
              type="button"
              className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${printingActivated ? "active" : ""}`}
              onClick={() => {
                updatePrintingHandler(true);
              }}
            >
              {t("enabled")}
            </button>
          </div>
        </div>

        <div className="item">
          <h5 className="subtitle">{t("skip_exchange")}</h5>
          <div className="text">{t("skip_exchange_info")}</div>
          <div className="mb-28 gap-12px d-flex filter-buttons">
            <button
              type="button"
              className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${!skipExchangeActivated ? "active" : ""}`}
              onClick={() => {
                updateSkipExchangeHandler(false);
              }}
            >
              {t("disabled")}
            </button>
            <button
              type="button"
              className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${skipExchangeActivated ? "active" : ""}`}
              onClick={() => {
                updateSkipExchangeHandler(true);
              }}
            >
              {t("enabled")}
            </button>
          </div>
        </div>

        <div className="item">
          <div className="item">
            <h5 className="subtitle">{t("printer_type_add")}</h5>
            <div className="text">{t("printer_type_selection_limited")}</div>

            <Dropdown
              onChange={onPrinterTypeChange}
              value={selectedPrinterType}
              options={printerTypeOptions}
            />
          </div>

          {printerProvider() === "printer_remote" && (
            <div className="item">
              <h5 className="subtitle">{t("activate_remote_printer")}</h5>
              <div className="text">{t("sunmi_only_compatible")}</div>

              <div className="mb-20">
                <Input
                  label={t("printer_ip")}
                  placeholder="127.0.0.1"
                  id="printerIp"
                  type="text"
                  value={printerIp}
                  onChange={(e) => {
                    dispatch({
                      type: "UPDATE_PRINTER_IP",
                      payload: e.target?.value,
                    });
                  }}
                />
              </div>
              <div className="mb-20">
                <Input
                  label={t("printer_port")}
                  placeholder="9100"
                  id="printerPort"
                  type="number"
                  defaultValue={printerPort}
                  onChange={(e) => {
                    dispatch({
                      type: "UPDATE_PRINTER_PORT",
                      payload: e.target?.value,
                    });
                  }}
                />
              </div>
            </div>
          )}

          <div className="item">
            <div className="mb-28 gap-12px d-flex filter-buttons">
              <button
                type="button"
                className={`themeBtn m-0 w-100 pos-submit-btn active`}
                onClick={updatePrinterTypeHandler}
              >
                {t("update")}
              </button>
            </div>
          </div>
        </div>

        <div className="item">
          <h5 className="subtitle">{t("activate_kiosk_mode")}</h5>
          <div className="text">{t("kiosk_only_compatible")}</div>
          <div className="mb-28 gap-12px d-flex filter-buttons">
            <button
              type="button"
              className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${!kioskActivated ? "active" : ""}`}
              onClick={() => {
                updateKioskHandler(false);
              }}
            >
              {t("disabled")}
            </button>
            <button
              type="button"
              className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${kioskActivated ? "active" : ""}`}
              onClick={() => {
                updateKioskHandler(true);
              }}
            >
              {t("enabled")}
            </button>
          </div>

          {kioskActivated && (
            <>
              <div className="text">{t("idle_screen_image_required")}</div>
              <div className="mb-28 gap-12px d-flex filter-buttons">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleImageSelect}
                />
                <button
                  type="button"
                  className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {!!idleImageUrl ? t("idle_screen_image_replace") : t("idle_screen_image_add")}
                </button>
                {!!idleImageUrl && (
                  <>
                    <button
                      type="button"
                      className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn`}
                      onClick={() => setImageModalOpen(true)}
                    >
                      {t("idle_screen_image_view")}
                    </button>
                    <button
                      type="button"
                      className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn`}
                      onClick={handleImageDeletion}
                    >
                      {t("delete")}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {kioskActivated && (
          <div className="item">
            <h5 className="subtitle">{t("pin_code_title")}</h5>
            <div className="text">{t("pin_code_description")}</div>
            <div className="mb-20">
              <Input
                label={t("pin_code_label")}
                placeholder="11223344"
                type="password"
                id="pinCode"
                value={pinCode}
                onChange={(e) => {
                  if (/^\d{0,8}$/.test(e.target.value)) {
                    dispatch({ type: "UPDATE_PIN_CODE", payload: e.target.value });
                  }
                }}
              />
            </div>
            <div className="mb-28 gap-12px d-flex filter-buttons">
              <button
                type="button"
                className={`themeBtn m-0 w-100 pos-submit-btn active`}
                onClick={updatePinCodeHandler}
              >
                {t("save")}
              </button>
            </div>
          </div>
        )}

        {paymentProvider() === "nepting" && (
          <div className="item">
            <h5 className="subtitle">{t("set_nepting_id")}</h5>
            <div className="text">{t("enter_nepting_id")}</div>

            <div className="mb-20">
              <Input
                label={t("nepting_id")}
                placeholder="Merchant ID"
                id="neptingId"
                type="text"
                value={neptingId}
                onChange={(e) => dispatch({ type: "UPDATE_NEPTING_ID", payload: e.target.value })}
              />
            </div>

            <div className="mb-20">
              <Input
                label={t("nepting_code")}
                placeholder="Merchant Code"
                id="neptingCode"
                type="text"
                value={neptingCode}
                onChange={(e) => dispatch({ type: "UPDATE_NEPTING_CODE", payload: e.target.value })}
              />
            </div>

            <div className="mb-28 gap-12px d-flex filter-buttons">
              <button
                type="button"
                className={`themeBtn m-0 w-100 pos-submit-btn active`}
                onClick={updateNeptingIdHandler}
              >
                {t("update")}
              </button>
            </div>
          </div>
        )}
        {paymentProvider() === "viva" && (
          <div className="item">
            <h5 className="subtitle">{t("set_viva_id")}</h5>
            <div className="text">{t("enter_viva_id")}</div>

            <div className="mb-20">
              <Input
                label={t("viva_id")}
                placeholder="Merchant ID"
                id="vivaMerchantId"
                type="text"
                value={vivaMerchantId}
                onChange={(e) => dispatch({ type: "UPDATE_VIVA_ID", payload: e.target.value })}
              />
            </div>

            <div className="mb-28 gap-12px d-flex filter-buttons">
              <button
                type="button"
                className={`themeBtn m-0 w-100 pos-submit-btn active`}
                onClick={updateVivaIdHandler}
              >
                {t("update")}
              </button>
            </div>
            <div className="item">
              <h5 className="subtitle">{t("show_viva_receipt")}</h5>
              <div className="text">{t("show_viva_receipt_description")}</div>
              <div className="mb-28 gap-12px d-flex filter-buttons">
                <button
                  type="button"
                  className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${!showVivaReceipt ? "active" : ""}`}
                  onClick={() => {
                    updateShowVivaReceiptHandler(false);
                  }}
                >
                  {t("disabled")}
                </button>
                <button
                  type="button"
                  className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${showVivaReceipt ? "active" : ""}`}
                  onClick={() => {
                    updateShowVivaReceiptHandler(true);
                  }}
                >
                  {t("enabled")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <HappyHourSetupModal
        isOpen={isHappyHourModalOpen}
        onClose={() => setIsHappyHourModalOpen(false)}
        selectedOrg={selectedOrg}
      />
    </div>
  );
}

export default ManageOrg;
